import { Alert, Form, Tooltip, Typography } from "antd";
import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import { HelpIcon } from "components/Icons";
import Vocabulary from "components/Vocabulary";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import {
  FeatureFlagContentValuesType,
  useFeatureFlag,
} from "contexts/FeatureFlagContext";
import _, { isEmpty } from "lodash";
import React, {
  ChangeEvent,
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getEmptyCancellableRequest } from "restAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import {
  FieldTypes,
  generateFormFields,
  getCustomFieldValue,
} from "utils/formUtils";
import { SEVERITY_NAMES } from "views/Findings/Constants";

import {
  CONFIG_UPDATE_WARNING_MESSAGE,
  CUSTOM_FILED_SUPPORTED_INTEGRATIONS,
  GenericIntegrationTicketDefaultValues,
  genericIntegrationTicketProductSubproductAppendAll,
} from "../genericIntegrationTicketConstants";
import {
  RenderCustomFields,
  ResolvedFindingStatusNames,
  generateResponse,
  integrationTicketingToolsProductOptionsMutator,
} from "../genericIntegrationTicketUtils";

type TransitionStatusType = {
  id: string;
  name: string;
  meta: Record<string, string>;
  otherProperties?: Record<string, any>;
};

const INITIAL_VALUES = {
  product: ["all"],
  subProduct: ["all"],
  critical: "Highest",
  high: "High",
  medium: "Medium",
  low: "Low",
  info: "Lowest",
};

const CONFIG_CHANGE_MESSAGE =
  "If You select a different Form, Organisation or Issue Type. Please review the settings and mapping on this screen before saving to ensure issues in the new Form, Organisation or Issue Type work as expected";

function getInitialData(
  data: GenericIntegrationTicketDefaultValues = {},
): GenericIntegrationTicketDefaultValues {
  if (!isEmpty(data)) {
    const issueTypeId =
      data.configMetaData?.level?.id || data.properties?.[data.issueType!];
    const { customFields } = data;
    _.forEach(customFields, (field) => {
      data[field.key! as keyof GenericIntegrationTicketDefaultValues] =
        getCustomFieldValue(field.type, field.defaultVal);
    });
    data = {
      ...data,
      labels: data.labels || ["Armorcode_Associated_Finding"],
      ...data.properties,
      subProduct: !isEmpty(data.subProductIds) ? data.subProductIds : ["all"],
      product: !isEmpty(data.product) ? data.product : ["all"],
      issueTypeId,
      // @ts-expect-error
      projectKeyId: data.configMetaData?.application?.id,
      ...(data.transitions
        ? { transitions: JSON.stringify(data.transitions) }
        : {}),
    };

    const defaultValues = { ...INITIAL_VALUES };
    Object.keys(data).forEach((key) => {
      // TODO resolve TS
      // @ts-expect-error
      if (_.isPlainObject(data[key]) && data[key].id) {
        // @ts-expect-error
        defaultValues[key] = data[key].id;
        // @ts-expect-error
      } else if (data[key]) {
        // @ts-expect-error
        defaultValues[key] = data[key];
      }
    });
    return defaultValues;
  }
  return INITIAL_VALUES;
}

const { Link } = Typography;

type ArcherConfigMappingFormProps = {
  loginConfigId: number;
  onUpdateConfigComponent: (value: boolean) => void;
  config?: GenericIntegrationTicketDefaultValues;
  url?: string;
  name: string;
};

const ArcherConfigMappingForm = (
  {
    loginConfigId,
    onUpdateConfigComponent,
    url,
    name,
    ...props
  }: ArcherConfigMappingFormProps,
  ref: ForwardedRef<any>,
) => {
  const [initialData] = useState(() => getInitialData(props.config));
  const isEdit = Boolean(props.config?.id);
  const [form] = Form.useForm();
  const [data, setData] = useState(initialData);
  const [isCustomFieldsLoading, setIsCustomFieldsLoading] = useState(false);
  const [transitionStatusOptions, setTransitionStatusOptions] = useState<
    TransitionStatusType[]
  >([]);
  const [isResolvedStatusDisabled, setIsResolvedStatusDisabled] = useState(
    !data.markResolved,
  );
  const [markReopen, setMarkReopen] = useState(data.markReopen);
  const [priorities, setPriorities] = useState([]);
  const featureFlags = useFeatureFlag();
  const toolName = name.toUpperCase();
  const customFieldSupportedInterations = useMemo(
    () =>
      _.compact(
        _.map(CUSTOM_FILED_SUPPORTED_INTEGRATIONS, (entry) => {
          const featureFlag = (entry as any).featureFlag;
          if (
            (featureFlag &&
              featureFlags[
                featureFlag as keyof FeatureFlagContentValuesType
              ]) ||
            !featureFlag
          ) {
            return entry.tool;
          }
          return null;
        }),
      ),
    [featureFlags],
  );

  const thisRef: MutableRefObject<any> = useRef();
  const issueTypesRef: MutableRefObject<any> = useRef(
    props.config?.properties &&
      props.config?.issueType &&
      props.config?.properties[props.config?.issueType]
      ? [
          {
            name: props.config.issueType,
            id: props.config.properties[props.config.issueType],
          },
        ]
      : [],
  );

  const projectKeyRef: MutableRefObject<any> = useRef();

  useEffect(() => {
    let cancelTransionStatusApi = _.noop;
    const getTransitionStatusOptions = async () => {
      if (!loginConfigId || !data.issueTypeId) {
        return false;
      }
      const requestPayload = {
        loginConfigId,
        issueTypeId: Number(data.issueTypeId),
      };
      const {
        promise: transitionStatusPromise,
        cancel: transitionStatusReject,
      } =
        ToolsIntegrationsAPI.getJiraCloseTransitionStatusOptions(
          requestPayload,
        );
      cancelTransionStatusApi = transitionStatusReject;
      const statusOptions = await transitionStatusPromise;
      setTransitionStatusOptions(statusOptions);
    };

    getTransitionStatusOptions();
    return cancelTransionStatusApi;
  }, [loginConfigId, data.issueTypeId]);

  const getIssueTypes = useCallback(
    (params) => {
      if (!loginConfigId || !data.projectKeyId) {
        return getEmptyCancellableRequest([]);
      }
      if (!data.projectKeyId) {
        return getEmptyCancellableRequest([]);
      }
      const { promise, cancel } =
        ToolsIntegrationsAPI.getTicketSystemToolIssueTypes({
          loginConfigId,
          projectKey: data.projectKeyId,
        });
      promise.then((res) => {
        //add searched issue type which does not exist on the same page
        issueTypesRef.current = _.uniqBy(
          [...res, ...issueTypesRef.current],
          "id",
        );
      });

      return { promise, cancel };
    },
    [loginConfigId, data.projectKeyId],
  );

  const onOk = async () => {
    await form.validateFields();
    const formData = { ...form.getFieldsValue(), ticketSystemType: "ARCHER" };
    formData.issueType = data.issueType;
    formData.ticketClosureStatus = _.isEmpty(formData.ticketClosureStatus)
      ? null
      : formData.ticketClosureStatus;
    formData.customFields = [];
    const configValues: any = await generateResponse(
      formData,
      featureFlags,
      toolName,
      props.config?.id,
      loginConfigId,
      url,
      thisRef.current?.getCustomFields(),
    );
    const issueTypeInPayload = issueTypesRef.current?.find(
      (_issueType: any) => _issueType.id === data.issueTypeId,
    );

    const projectKeyMeta = projectKeyRef.current?.find(
      (project: any) => project.name === formData.projectKey,
    );

    const reopenStatusMeta = transitionStatusOptions.find(
      (status: any) => status.name === formData.reopenStatus,
    );
    const resolvedStatusMeta = transitionStatusOptions.find(
      (status: any) => status.name === formData.resolvedStatus,
    );

    const meta = {
      project: projectKeyMeta,
      issueType: issueTypeInPayload,
      reopenStatus: reopenStatusMeta?.meta,
      resolvedStatus: resolvedStatusMeta?.meta,
      properties: priorities,
    };
    configValues.configValues.properties[formData.issueType] =
      issueTypeInPayload?.id;
    configValues.configValues.issueTypeId = data.issueTypeId;
    configValues.configValues.metaData = meta;

    await ToolsIntegrationsAPI[
      !isEdit ? "integrateTicketSystemTool" : "updateTicketSystemToolMapping"
    ](configValues.configValues, props.config?.id).promise;
    message.success(`Archer Mapping saved`);
    setData({});
    onUpdateConfigComponent && onUpdateConfigComponent(false);
  };

  useEffect(() => {
    if (!data.issueTypeId) {
      return;
    }
    setPriorities([]);
    const { cancel, promise } =
      ToolsIntegrationsAPI.getTicketSystemToolPriorities({
        issueTypeId: Number(data.issueTypeId),
        loginConfigId,
      });
    promise.then((response) => {
      if (!isEdit) {
        const severities: any = {};
        const severityNames = Object.keys(SEVERITY_NAMES);
        response.forEach((entry: any, index: number) => {
          severities[severityNames[index]] = entry.name;
        });

        // Fill in any missing severities in response(sometime expected) with the last known value
        if (severityNames.length > response.length) {
          for (
            let index = response.length;
            index < severityNames.length;
            index++
          ) {
            severities[severityNames[index]] =
              severities[severityNames[index - 1]];
          }
        }
        form.setFieldsValue(severities);
      }
      setPriorities(response);
    });
    return cancel;
  }, [isEdit, data.issueTypeId, loginConfigId, form]);

  const resetSubProductsField = useCallback(
    (values) => {
      setData((data: GenericIntegrationTicketDefaultValues) => ({
        ...data,
        product: values,
      }));
      form.setFieldsValue({ subProduct: ["all"] });
    },
    [form],
  );

  const commonSeverityArcherProps = useMemo(
    () => ({
      colSpan: 5,
      className: "m-b-0",
      type: FieldTypes.SELECT,
      style: { flex: "0 0 20%" },
      options: priorities,
      loading: _.isNil(priorities),
      required: true,
      props: {
        optionsMutator: parseOptions,
      },
    }),
    [priorities],
  );

  const modalProps = {
    title: `${isEdit ? "Edit" : "Add"} Archer Mapping`,
    width: MODAL_SIZE_LARGE,
    onOk,
    disabled:
      !hasPermission(
        isEdit
          ? PermissionSetMap.EDIT_TOOL_CONFIG
          : PermissionSetMap.WRITE_TOOLS,
      ) || isCustomFieldsLoading,
  };

  // if more than one product or All product is selected then subproduct should be disabled
  // this would work as previous, just will get the product id from array, as product is multi select
  const productId =
    data.product && data.product[0] !== "all" ? data.product[0] : null;

  const onCreateSubproduct = useCallback(
    (createdSubproduct) => {
      const subProductValues = form.getFieldValue("subProduct");
      form.setFieldsValue({
        subProduct: [...subProductValues, createdSubproduct.id],
      });
      setData((prevdata) => ({
        ...prevdata,
        subProduct: [...subProductValues, createdSubproduct.id],
      }));
    },
    [form],
  );

  const onCreateProduct = useCallback(
    (createdProduct) => {
      const productsAfterCreate = [
        ...form.getFieldValue("product"),
        createdProduct.id,
      ];
      form.setFieldsValue({
        product: productsAfterCreate,
        subProduct: ["all"],
      });
      setData((data) => ({
        ...data,
        product: productsAfterCreate,
        subProduct: ["all"],
      }));
    },
    [form],
  );

  const getProjectKeys = useCallback(() => {
    if (!loginConfigId) {
      return getEmptyCancellableRequest([]);
    }
    const { promise, cancel } =
      ToolsIntegrationsAPI.getTicketSystemToolProjects(loginConfigId);
    promise.then((response) => {
      projectKeyRef.current = response;
    });
    return { promise, cancel };
  }, [loginConfigId]);

  const productSubProductFields = useMemo(
    () => [
      {
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        // disabled:isEdit,
        props: {
          dropdownType: "product",
          showAddButton: true,
          optionsMutator: (
            options: [{ label: string; value: number | string }],
          ) =>
            integrationTicketingToolsProductOptionsMutator(
              options,
              form.getFieldValue("product"),
            ),
          onChange: resetSubProductsField,
          onCreate: onCreateProduct,
          mode: "multiple",
        },
      },
      {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        disabled: data.product && data.product.length > 1,
        props: {
          productId: productId,
          showAddButton: Boolean(productId),
          dropdownType: "subProduct",
          onCreate: onCreateSubproduct,
          onChange: (values: any[]) => {
            if (values.includes("all")) {
              form.setFieldsValue({ subProduct: ["all"] });
            }
          },
          optionsMutator: (options: any[]) => {
            const updatedOptions =
              genericIntegrationTicketProductSubproductAppendAll(options);
            const selectedValues = form.getFieldValue("subProduct") || [];
            const isAllSelected = selectedValues.includes("all");

            return updatedOptions.map((option) => ({
              ...option,
              disabled: isAllSelected && option.value !== "all",
            }));
          },

          mode: "multiple",
        },
      },
    ],
    [
      form,
      productId,
      resetSubProductsField,
      onCreateSubproduct,
      onCreateProduct,
      data.product,
    ],
  );

  const handleMarkResolveChecked = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const checked = !data.issueType || !event.target.checked;
      setIsResolvedStatusDisabled(checked);
    },
    [data.issueType],
  );

  const handleMarkReopen = useCallback((event) => {
    const checked = event.target.checked;
    setMarkReopen(checked);
  }, []);

  const findingField = useMemo(
    () => [
      {
        render: (props: any) => <div {...props} />,
        className: "p-y-sm",
      },
      {
        name: "markResolved",
        label: (
          <>
            Close Ticket when Finding is <Vocabulary name={"mitigated"} />
            <Tooltip
              title={
                <>
                  Automatically closes Archer ticket when an issue is fixed. The
                  other states that may trigger ticket close are{" "}
                  {<ResolvedFindingStatusNames />} based on the global
                  configuration as provided <br />
                  <Link href="#/globalsettings?activeStep=step17">
                    Click here
                  </Link>
                </>
              }
              placement="top"
            >
              <HelpIcon />
            </Tooltip>
          </>
        ),
        type: FieldTypes.BOOLEAN,
        colSpan: 12,
        inline: true,
        props: {
          onChange: handleMarkResolveChecked,
        },
      },
      {
        name: "resolvedStatus",
        label: "Close Transition Name",
        help: `Close Transition Name should be set as the final (close) status in your defined Archer workflow for your project. For example 'CLOSED', 'RESOLVED' or 'DONE'.`,
        type: FieldTypes.SELECT,
        options: transitionStatusOptions,
        required: !isResolvedStatusDisabled,
        disabled: isResolvedStatusDisabled,
        colSpan: 12,
        props: {
          optionsMutator: parseOptions,
        },
      },
      {
        name: "markReopen",
        label: "Re-open ticket if finding remains unresolved from a scan",
        type: FieldTypes.BOOLEAN,
        colSpan: 12,
        inline: true,
        props: {
          onChange: handleMarkReopen,
        },
        className: "m-t-l",
      },
      {
        name: "reopenStatus",
        label: "Re-open Transition Name",
        type: FieldTypes.SELECT,
        options: transitionStatusOptions,
        disabled: !markReopen,
        required: markReopen,
        colSpan: 12,
        help: (
          <>
            Archer status(transition) which can be directly assigned from closed
            status(transition)
          </>
        ),
        props: {
          allowClear: true,
          optionsMutator: parseOptions,
        },
      },
    ],
    [
      handleMarkResolveChecked,
      isResolvedStatusDisabled,
      transitionStatusOptions,
      handleMarkReopen,
      markReopen,
    ],
  );

  const fields = useMemo(
    () => [
      ...productSubProductFields,
      {
        name: "projectKey",
        label: "Application",
        colSpan: 12,
        type: FieldTypes.SELECT,
        required: true,
        props: {
          api: getProjectKeys,
          parseSelectOptions: parseOptions,
          onChange: (value: string) => {
            isEdit && message.warn(CONFIG_UPDATE_WARNING_MESSAGE);
            form.setFieldsValue({
              issueType: undefined,
            });
            setData((prevData) => ({
              ...prevData,
              projectKey: value,
              projectKeyId: _.find(projectKeyRef.current, {
                name: value,
              })?.id,
              issueType: undefined,
              issueTypeId: undefined,
            }));
          },
        },
      },
      {
        name: "issueType",
        label: "Level",
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        required: true,
        props: {
          api: getIssueTypes,
          resetOptionsProp: getIssueTypes,
          onChange: (
            value: string,
            listField: any,
            selectedOption: { label: string; value: string },
          ) => {
            isEdit && message.warn(CONFIG_CHANGE_MESSAGE);
            setData((data: GenericIntegrationTicketDefaultValues) => ({
              ...data,
              issueType: selectedOption?.label,
              issueTypeId: selectedOption?.value,
            }));
          },
        },
      },
      ...(data.projectKey && data.issueType && _.isEmpty(priorities)
        ? [
            {
              name: "",
              label: "",
              render: () => (
                <Alert
                  type="info"
                  className="m-b-xs"
                  showIcon
                  message={`The Archer Priority field is not available as part of ${data.issueType} for ${data.projectKey}. The findings severity mapping will not be supported.`}
                />
              ),
            },
          ]
        : []),
      ...(data.issueType && !_.isEmpty(priorities)
        ? [
            {
              label: `ArmorCode Severity -> Archer Criticality`,
              type: FieldTypes.GROUP,
              required: false,
              fields: [
                {
                  name: "critical",
                  label: <Vocabulary name="critical" />,
                  ...commonSeverityArcherProps,
                },
                {
                  name: "high",
                  label: <Vocabulary name="high" />,
                  ...commonSeverityArcherProps,
                },
                {
                  name: "medium",
                  label: <Vocabulary name="medium" />,
                  ...commonSeverityArcherProps,
                },
                {
                  name: "low",
                  label: <Vocabulary name="low" />,
                  ...commonSeverityArcherProps,
                },
                {
                  name: "info",
                  label: <Vocabulary name="info" />,
                  ...commonSeverityArcherProps,
                },
              ],
            },
          ]
        : []),
      ...findingField,
    ],
    [
      commonSeverityArcherProps,
      findingField,
      getIssueTypes,
      productSubProductFields,
      isEdit,
      getProjectKeys,
      form,
      data.projectKey,
      data.issueType,
      priorities,
    ],
  );

  //TODO need to resolve TS
  return (
    <BaseModal
      ref={ref}
      modalProps={modalProps}
      // @ts-expect-error
      afterClose={props.afterClose}
    >
      <Form
        form={form}
        initialValues={{ ...data, issueType: data.issueTypeId }}
        layout="vertical"
      >
        {generateFormFields(fields)}
      </Form>
      {customFieldSupportedInterations.indexOf("Archer") !== -1 ? (
        <>
          <RenderCustomFields
            // @ts-expect-error
            form={form}
            ref={thisRef}
            // Archer expects projectKeyId in projectKey param
            initialValues={{ ...data, projectKey: data.projectKeyId }}
            loginConfigId={loginConfigId}
            changeMode={isEdit}
            configId={props.config?.id}
            toolName={toolName}
            onLoadingChange={setIsCustomFieldsLoading}
          />
        </>
      ) : null}
    </BaseModal>
  );
};

export default forwardRef(ArcherConfigMappingForm);

const parseOptions = (options: [{ id: number | string; name: string }]) => {
  return options.map((option) => ({
    label: option.name,
    value: option.name,
  }));
};
